import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiLogOut, FiSearch, FiCheckCircle, FiXCircle, FiActivity } from "react-icons/fi";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { VOLATILITY_SYMBOLS, fetchVolatilitySymbols, subscribeToTicks } from "../utils/deriv";

const scaleConfidence = raw => Math.round(85 + (raw / 100) * 14);

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [adminName, setAdminName] = useState("Guru");

  // ── AUTH CHECK ────────────────────────────────────────────────────────────
  useEffect(() => {
    const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");

    if (!adminInfo?.email) {
      navigate("/admin/login", { replace: true });
      return;
    }

    setAdminName(adminInfo.name || adminInfo.email.split("@")[0]);
  }, [navigate]);

  // ── FETCH ALL USERS ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, "deriv_users"));
        setUsers(snapshot.docs.map((d) => ({ uid: d.id, ...d.data() })));
      } catch (err) {
        console.error("Admin fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // ── COMPUTED LISTS ────────────────────────────────────────────────────────
  const pendingUsers = useMemo(
    () => users.filter(u => !u.approved && u.status === "pending"),
    [users]
  );
  const approvedUsers = useMemo(
    () => users.filter(u => u.approved && u.status === "active"),
    [users]
  );
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      u =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.loginid?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  // ── ACTIONS ───────────────────────────────────────────────────────────────
  const handleAction = async (uid, action) => {
    if (actionLoading[uid]) return;

    setActionLoading(prev => ({ ...prev, [uid]: true }));

    try {
      const now = new Date().toISOString();
      const userRef = doc(db, "deriv_users", uid);

      if (action === "approve") {
        await updateDoc(userRef, {
          approved: true,
          approvedAt: now,
          status: "active",
        });
      } else {
        await updateDoc(userRef, {
          approved: false,
          status: "rejected",
          rejectedAt: now,
          rejectReason: "Manual rejection",
        });
      }

      const snapshot = await getDocs(collection(db, "deriv_users"));
      setUsers(snapshot.docs.map((d) => ({ uid: d.id, ...d.data() })));
    } catch (err) {
      console.error(`Admin ${action} error:`, err);
      alert(`Failed to ${action} user. Try again.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [uid]: false }));
    }
  };

  const logout = () => {
    localStorage.removeItem("adminInfo");
    navigate("/admin/login", { replace: true });
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#01030b] via-[#030712] to-[#01030b] flex items-center justify-center text-cyan-400">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-cyan-700/40 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4 shadow-lg shadow-cyan-600/50"></div>
          <p className="text-lg font-semibold">Loading Guru Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#01030b] via-[#030712] to-[#01030b] text-white relative overflow-hidden">

      {/* Background aura */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,#00fff2_0%,transparent_60%)] animate-pulse-slow"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,#ff6aff_0%,transparent_60%)] animate-pulse-slow delay-2000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500">
              Guru Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Welcome back, <span className="text-cyan-300 font-medium">{adminName}</span></p>
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 px-6 py-3 bg-red-900/60 hover:bg-red-800/70 border border-red-700/60 rounded-full font-medium text-sm transition shadow-lg shadow-red-900/50"
          >
            <FiLogOut /> Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {[
            { label: "Pending", value: pendingUsers.length, color: "yellow" },
            { label: "Approved", value: approvedUsers.length, color: "green" },
            { label: "Total Users", value: users.length, color: "cyan" },
          ].map(({ label, value, color }) => (
            <div key={label} className={`bg-[#0a1325]/70 backdrop-blur-md rounded-3xl border border-${color}-900/50 p-6 text-center shadow-md shadow-${color}-700/30 transition transform hover:scale-105`}>
              <p className="text-sm text-gray-400 mb-2">{label}</p>
              <p className={`text-4xl font-black text-${color}-400`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-8 border-b border-cyan-950/40 pb-4">
          {["pending", "approved", "all", "matchesTool"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                activeTab === tab
                  ? "bg-cyan-600/50 text-cyan-100 border border-cyan-500/60 shadow-cyan-900/40 shadow-lg"
                  : "bg-[#0a1325]/60 text-gray-300 hover:bg-[#0a1325]/80 border border-cyan-900/40"
              }`}
            >
              {tab === "pending"
                ? `Pending (${pendingUsers.length})`
                : tab === "approved"
                ? `Approved (${approvedUsers.length})`
                : tab === "all"
                ? `All (${users.length})`
                : <><FiActivity /> Matches Tool</>}
            </button>
          ))}
        </div>

        {/* Search */}
        {activeTab !== "matchesTool" && (
          <div className="relative mb-8 max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name, email, or login ID..."
              className="w-full pl-11 pr-4 py-3 bg-[#050814]/80 border border-cyan-900/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition shadow-inner"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="bg-[#0a1325]/60 backdrop-blur-md rounded-3xl border border-cyan-900/40 p-6 min-h-[500px] shadow-lg shadow-cyan-900/30">
          {activeTab === "pending" && renderUserGrid(pendingUsers, true)}
          {activeTab === "approved" && renderUserGrid(approvedUsers, true)}
          {activeTab === "all" && renderUserGrid(filteredUsers, true)}
          {activeTab === "matchesTool" && <MatchesToolPreview />}
        </div>
      </div>
    </div>
  );

  // ── USER GRID ────────────────────────────────────────────────────────────
  function renderUserGrid(list, showActions = false) {
    if (!list.length) {
      return (
        <div className="text-center py-16 text-gray-400">
          <p className="text-xl">No users found</p>
          {searchTerm && <p className="mt-2">Try a different search term</p>}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map(user => (
          <div key={user.uid} className="bg-[#050814]/70 p-6 rounded-3xl border border-cyan-900/40 hover:border-cyan-700/60 transition-all shadow-lg hover:shadow-cyan-800/40">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-bold text-cyan-300">{user.name || "Unnamed"}</p>
                <p className="text-sm text-gray-400">{user.email}</p>
                <p className="text-xs text-gray-500 mt-0.5">ID: {user.loginid}</p>
              </div>

              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                user.approved
                  ? "bg-green-900/50 text-green-300 border border-green-700/40"
                  : "bg-yellow-900/50 text-yellow-300 border border-yellow-700/40"
              }`}>
                {user.approved ? "Approved" : "Pending"}
              </div>
            </div>

            <div className="text-sm text-gray-400 space-y-1 mb-4">
              <p>Status: <span className="text-gray-200">{user.status}</span></p>
              {user.approved && <p>Approved: <span className="text-cyan-300">{new Date(user.approvedAt).toLocaleDateString()}</span></p>}
            </div>

            {showActions && !user.approved && (
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleAction(user.uid, "approve")}
                  disabled={actionLoading[user.uid]}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 py-2.5 rounded-xl font-medium disabled:opacity-50 transition shadow-sm"
                >
                  <FiCheckCircle /> Approve
                </button>

                <button
                  onClick={() => handleAction(user.uid, "reject")}
                  disabled={actionLoading[user.uid]}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 py-2.5 rounded-xl font-medium disabled:opacity-50 transition shadow-sm"
                >
                  <FiXCircle /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── MATCHES TOOL PREVIEW ──────────────────────────────────────────────────
  function MatchesToolPreview() {
    const [volatilities, setVolatilities] = useState(VOLATILITY_SYMBOLS);
    const [ticks, setTicks] = useState({});
    const [signals, setSignals] = useState({});
    const [phase, setPhase] = useState("countdown");
    const [timer, setTimer] = useState(15);
    const [showEvenOdd, setShowEvenOdd] = useState(true);
    const ticksRef = useRef({});

    useEffect(() => { ticksRef.current = ticks; }, [ticks]);

    useEffect(() => {
      let cancelled = false;
      fetchVolatilitySymbols().then((symbols) => {
        if (!cancelled) setVolatilities(symbols);
      });
      return () => { cancelled = true; };
    }, []);

    useEffect(() => {
      const sockets = volatilities.map(({ symbol }) =>
        subscribeToTicks(symbol, (quote) => {
          const digit = Math.floor(quote * 10) % 10;
          setTicks(prev => {
            const updated = { ...prev, [symbol]: [...(prev[symbol] || []), digit].slice(-50) };
            if (phase === "countdown") generateSignal(symbol, updated[symbol]);
            return updated;
          });
        })
      );
      return () => sockets.forEach(ws => ws.close());
    }, [phase, volatilities]);

    useEffect(() => {
      const interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            if (phase === "countdown") { setPhase("entry"); return 5; }
            if (phase === "entry") { setPhase("trade"); return 5; }
            if (phase === "trade") { setPhase("countdown"); volatilities.forEach(v => generateSignal(v.symbol, ticksRef.current[v.symbol])); return 15; }
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }, [phase, volatilities]);

    const generateSignal = (symbol, history) => {
      if (!history?.length) return;
      const recent = history.slice(-50);
      const freq = Array(10).fill(0);
      recent.forEach(d => freq[d]++);
      let maxCount = 0, bestDigit = 0;
      freq.forEach((count, digit) => { if (count > maxCount) { maxCount = count; bestDigit = digit; } });
      const dominance = maxCount / recent.length;
      const confidence = scaleConfidence(dominance * 100);
      const overUnder = bestDigit % 2 === 0 ? "EVEN" : "ODD";
      setSignals(prev => { const prevSignal = prev[symbol]; if (prevSignal?.digit === bestDigit) return prev; return { ...prev, [symbol]: { digit: bestDigit, confidence, overUnder, lastDigits: recent.slice(-5) } }; });
    };

    return (
      <div className="px-2 sm:px-4 py-6">
        <h2 className="text-2xl sm:text-3xl font-black text-cyan-400 mb-6 text-center">Matches Tool – Live Preview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {volatilities.map(({ name, symbol }) => {
            const signal = signals[symbol] || {};
            const showEntry = phase === "entry" && signal.digit !== undefined;
            const showTrade = phase === "trade" && signal.digit !== undefined;

            return (
              <div key={symbol} className="bg-[#0a1325]/80 backdrop-blur-md border border-cyan-900/50 rounded-2xl p-4 flex flex-col items-center text-center shadow-lg shadow-cyan-950/30 hover:border-cyan-600/60 transition-all">
                <h3 className="text-sm font-semibold text-cyan-300 mb-3 truncate w-full">{name}</h3>
                {showEntry || showTrade ? (
                  <>
                    <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-2xl md:text-3xl font-extrabold mb-2 shadow-inner ${signal.digit % 2 === 0 ? "bg-gradient-to-tr from-cyan-500 to-blue-500" : "bg-gradient-to-tr from-indigo-600 to-purple-500"}`}>{signal.digit}</div>
                    <p className="text-yellow-300 text-sm md:text-base font-semibold mb-1">{signal.confidence}%</p>
                    {showEvenOdd && <p className="text-xs md:text-sm text-gray-300 mb-1">{signal.overUnder}</p>}
                    {showEntry ? <p className="text-sm md:text-base text-cyan-300 font-medium">Entry in {timer}s</p> : <p className="text-lg md:text-xl text-green-400 font-extrabold animate-pulse">TRADE</p>}
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#050814] border border-cyan-900/40 flex items-center justify-center text-xl md:text-2xl font-bold text-gray-400 mb-2">{timer}s</div>
                    <p className="text-xs md:text-sm text-gray-400">Next prediction</p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Even/Odd Toggle */}
        <div className="mt-8 flex justify-center">
          <div className="bg-[#0a1325]/80 backdrop-blur-md border border-cyan-900/50 rounded-full px-5 py-2.5 flex items-center gap-4">
            <span className="text-sm text-gray-300">Show Even/Odd</span>
            <button onClick={() => setShowEvenOdd(!showEvenOdd)} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${showEvenOdd ? "bg-cyan-600 text-white" : "bg-gray-700 text-gray-300"}`}>{showEvenOdd ? "ON" : "OFF"}</button>
          </div>
        </div>
      </div>
    );
  }
}
